import React, { useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * "Request Cancellation" button — drop on Sales Invoice / Purchase Order /
 * Delivery Note / Payment detail rows.
 *
 * Visibility rules (matches backend):
 *   • Hidden if the doc is already cancelled.
 *   • Hidden if a cancellation request is already pending.
 *   • Hidden for non-admins who aren't the document creator (server also
 *     enforces this — UI is a polite filter).
 *
 * Props:
 *   docKind: 'sales_invoice' | 'purchase_order' | 'delivery_note' | 'payment'
 *   docId:   the document's primary key (invoice_number, po.id, dn_number, payment.id)
 *   doc:     the doc record (used for visibility checks + auto-refresh)
 *   onDone:  callback invoked after a successful request (refresh the list)
 */
const RequestCancellationButton = ({ docKind, docId, doc = {}, onDone, size = 'sm', label = 'Request Cancellation' }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isCreator = (doc?.created_by || doc?.requested_by) === user?.id;
  const alreadyCancelled =
    (doc?.payment_status || doc?.status || '').toLowerCase() === 'cancelled' ||
    doc?.cancellation_status === 'cancelled';
  const cancellationPending = doc?.cancellation_status === 'pending';

  // Hide if not allowed / not applicable
  if (alreadyCancelled || cancellationPending) return null;
  if (!isAdmin && !isCreator) return null;

  const submit = async () => {
    if (reason.trim().length < 3) {
      setError('Reason must be at least 3 characters.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post('/cancellation-requests', {
        doc_kind: docKind,
        doc_id: docId,
        reason: reason.trim(),
        note: note.trim() || null,
      });
      setOpen(false);
      setReason('');
      setNote('');
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit cancellation request.');
    } finally {
      setBusy(false);
    }
  };

  const sizeCls = size === 'sm'
    ? 'text-xs px-2 py-1'
    : 'text-sm px-3 py-1.5';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${sizeCls} rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 font-medium`}
        data-testid={`req-cancel-${docKind}-${docId}`}
      >
        🗑 {label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <div>
              <h2 className="text-lg font-semibold text-rose-700">Request Cancellation</h2>
              <p className="text-xs text-slate-500">
                Submits a cancellation request for an approver to review. The document stays active until approved.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason <span className="text-rose-600">*</span></label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="e.g. Customer cancelled the order"
                data-testid="cancel-reason-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Supporting note <span className="text-slate-400 text-xs">(optional)</span></label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="Any extra context for the approver"
                data-testid="cancel-note-input"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{error}</div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"
              >Cancel</button>
              <button
                onClick={submit}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                data-testid="cancel-request-submit"
              >{busy ? 'Submitting…' : 'Submit request'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestCancellationButton;
